import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES, MARQUEE } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import Marquee from "react-fast-marquee";

const EASE = [0.22, 1, 0.36, 1];
const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, ease: EASE, delay: 0.15 + i * 0.14 } }),
};

const HEADLINE = ["Connecting Quality Indian Products", "with Global Markets"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden grain">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.makhanaPond}
          alt="Makhana pond with lotus plants — AquaFionix integrated aquaculture"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-[#030D18]/85 via-[#030D18]/45 to-[#030D18]" />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(120%_80%_at_15%_10%,rgba(0,240,255,0.12),transparent_55%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-28 lg:pt-44 min-h-[100svh] flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-950/40 px-4 py-1.5 backdrop-blur"
        >
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-200">
            AquaFionix Integrated Aquaculture Pvt. Ltd.
          </span>
        </motion.div>

        <h1 className="font-display mt-6 text-4xl sm:text-6xl lg:text-[5.2rem] font-extrabold leading-[1.03] tracking-tight max-w-5xl">
          {HEADLINE.map((l, i) => (
            <span key={l} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {i === 1 ? <span className="text-gradient-cyan">{l}</span> : <span className="text-white">{l}</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-6 max-w-2xl text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed"
        >
          AquaFionix is building RAS-assisted, high-tech shrimp and crab farming with a zero-waste approach — alongside export-oriented supply of premium Indian Makhana (Fox Nuts), our priority for global markets.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Link
            to="/makhana-export"
            data-testid="hero-explore-makhana-button"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 text-sm font-semibold text-[#1a0f00] shadow-[0_10px_30px_rgba(245,158,11,0.3)] hover:shadow-[0_14px_40px_rgba(245,158,11,0.45)] hover:-translate-y-0.5 transition-all duration-300"
          >
            Explore Makhana
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <button
            onClick={() => scrollToId("inquiry")}
            data-testid="hero-export-inquiry-button"
            className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/10 hover:border-cyan-400/40 transition-all duration-300"
          >
            Export Inquiry
            <ArrowRight className="h-4 w-4 text-cyan-300 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 inset-x-0 z-20 border-t border-white/10 bg-[#030D18]/70 backdrop-blur-sm py-3">
        <Marquee gradient={false} speed={38} className="overflow-hidden">
          {MARQUEE.concat(MARQUEE).map((w, i) => (
            <span key={i} className="mx-6 inline-flex items-center gap-3 text-sm text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/70" />
              <span className="uppercase tracking-[0.15em]">{w}</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
